import { ObjectId } from 'mongodb';
import { ChannelAccountID } from '../channel';
import { DateTime } from '../datetime';

/**
 * @deprecated Use `Exports.ParamsDateRange` instead
 */
export type ExportParamsDateRange<isJson extends boolean = false> = Exports.ParamsDateRange<isJson>;

/**
 * @deprecated Use `Exports.Params` instead
 */
export type ExportParams<isJson extends boolean = false> = Exports.Params<isJson>;

/**
 * @deprecated Use `Exports.Entity` instead
 */
export type ExportEntity<IsJSON extends boolean = false> = Exports.Entity<IsJSON>;

export namespace Exports {

    /**
     * Minimal delay between current time and scheduled export time
     */
    export const SCHEDULE_MIN_DELAY = 1000 * 60 * 30;

    export type Params<isJson extends boolean = false> = {
        dateRange?: ParamsDateRange<isJson>;
        /**
         * Channel accounts to export.
         * Should include at least one channel account when specified.
         */
        channelsAccounts?: ChannelAccountID[];
        includeAttachments?: boolean;
        includeEmptyChats?: boolean;
    };

    export type ParamsDateRange<isJson extends boolean = false> = {
        start: isJson extends true ? string : Date;
        end: isJson extends true ? string : Date;
    };

    export enum Status {
        requested = 'requested',
        in_progress = 'in_progress',
        done = 'done',
        failed = 'failed',
    }

    export interface Entity<IsJSON extends boolean = false> {
        _id: IsJSON extends true ? string : ObjectId;
        status: ExportStatus;
        fileID?: string;
        /**
         * Date when export is scheduled to start
         */
        scheduled?: IsJSON extends true ? string : Date;
        /**
         * Date when export was requested by user (but not necessarily started yet)
         */
        createdAt: IsJSON extends true ? string : Date;
        /**
         * Date when export was started
         */
        startedAt?: IsJSON extends true ? string : Date;
        /**
         * Date when export was finished
         */
        finishedAt?: IsJSON extends true ? string : Date;
        params?: Params<IsJSON>;
        size?: number;
    }

    export type ExportDeleteEntityResponse = {
        success: boolean;
    };

    export namespace Validation {

        export type Problem = TooSoonProblem | TooManyChatsProblem | TimeOfTheDayProblem;

        /**
         * Export requested too early
         */
        export type TooSoonProblem = {
            type: ProblemType.tooSoon;
            /**
             * Timestamp when next export would be allowed due to configured policy
             */
            nextAllowed: number;
            /**
             * What exactly caused the problem
             * - `doneExport` - Previous export that was already done recently
             * - `scheduledExport` - Another scheduled export that too close to requested one
             * - `minScheduleDelay` - `SCHEDULE_MIN_DELAY` value is not met
             */
            reason: 'doneExport' | 'scheduledExport' | 'minScheduleDelay';
        };

        export type TooManyChatsProblem = {
            type: ProblemType.tooManyChats;
            /**
             * Number of chats that requested to be exported
             */
            chatsCount: number;
            /**
             * Maximum number of chats allowed to export
             */
            limit: number;
        };

        export type TimeOfTheDayProblem = {
            type: ProblemType.timeOfTheDay;
            /**
             * Times when export is not allowed
             */
            exceptTime: {
                weekday: DateTime.WEEKDAY;
                period: DateTime.DayTimeframe;
            };
            weekSchedule: DateTime.WeekSchedule;
        };

        export enum ProblemType {
            tooManyChats = 'tooManyChats',
            tooSoon = 'tooSoon',
            timeOfTheDay = 'timeOfTheDay',
        }

    }

}

/**
 * @deprecated Use `Exports.Status` instead
 */
export import ExportStatus = Exports.Status;
